/**
 * POSLayout - Peace of Sleep Hotel Portal Layout
 *
 * Mirrors DesktopLayout structure with gold POS theme.
 * Same UX patterns, just branded for hotel customers.
 *
 * Brand Theme:
 * - Linen: #EAE7E1 (backgrounds)
 * - Gold: #BD982F (accents, buttons)
 * - Black: #000000 (text, headers)
 * - Light Grey: #C1BFBD (secondary text)
 */

import { PropsWithChildren, useState, useEffect, ChangeEvent } from 'react';
import { NavLink, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePOS } from '../../contexts/POSContext';
import './pos-theme.css';
import { useSidebarHidden } from '../../hooks/useSidebarHidden';
import { PanelIcon } from '../../components/PanelIcon';

// POS Navigation items - flat list (no collapsible groups)
const posNavItems = [
  { path: '', label: 'Dashboard', icon: '📊', end: true },
  { path: '/services', label: 'Services', icon: '✨', end: false },
  { path: '/rooms', label: 'Rooms', icon: '🛏️', end: false },
  { path: '/properties', label: 'Properties', icon: '🏨', end: false },
  { path: '/managers', label: 'Managers', icon: '👥', end: false },
  { path: '/certificates', label: 'Certificates', icon: '📜', end: false },
];

/**
 * POSLayout - Branded layout for hotel managers
 * Mirrors DesktopLayout structure with gold theme
 */
function POSLayout({ children }: PropsWithChildren) {
  const { user, signOut, getPortalAccess } = useAuth();
  const portalAccess = getPortalAccess();
  const { userProperties, selectedPropertyId, setSelectedPropertyId, propertiesLoading } = usePOS();
  const { propertySlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { hidden: sidebarHidden, toggle: toggleSidebar } = useSidebarHidden();

  // Find property from slug and sync with context
  const currentProperty = userProperties.find(p =>
    p.property_code?.toLowerCase() === propertySlug?.toLowerCase() ||
    p.property_name?.toLowerCase().replace(/\s+/g, '-') === propertySlug?.toLowerCase()
  );

  // Set selected property based on URL slug
  useEffect(() => {
    if (!propertiesLoading && currentProperty && currentProperty.id !== selectedPropertyId) {
      setSelectedPropertyId(currentProperty.id);
    }
  }, [propertySlug, currentProperty, propertiesLoading, selectedPropertyId, setSelectedPropertyId]);

  // Build nav paths with property slug
  const getNavPath = (itemPath: string) => {
    return `/pos/${propertySlug}${itemPath}`;
  };

  // Get current page title
  const currentPage = posNavItems.find(item => {
    const fullPath = getNavPath(item.path);
    return item.end ? location.pathname === fullPath : location.pathname.startsWith(fullPath);
  });

  // Handle property switch
  const handlePropertyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    navigate(`/pos/${newSlug}`);
  };

  if (propertiesLoading) {
    return (
      <div className="pos-loading">
        <div className="pos-loading-content">
          <div className="pos-loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`portal-layout pos-themed${sidebarHidden ? ' sidebar-hidden' : ''}`}>
      {/* Sidebar - mirrors DesktopLayout structure */}
      <aside className={`sidebar pos-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header pos-sidebar-header">
          <div className="sidebar-brand pos-brand-name">PEACE OF SLEEP</div>
          <div className="sidebar-subtitle pos-brand-tagline">The Quiet Standard Of Trust</div>
        </div>

        {/* Property Selector - only if multiple properties */}
        {userProperties.length > 1 && (
          <div className="pos-property-selector">
            <select
              value={propertySlug}
              onChange={handlePropertyChange}
              className="pos-property-select"
            >
              {userProperties.map(p => (
                <option key={p.id} value={p.property_code?.toLowerCase()}>
                  {p.property_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Current Property Display */}
        {currentProperty && (
          <div className="pos-current-property">
            <span className="pos-property-name">{currentProperty.property_name}</span>
            {currentProperty.access_level && (
              <span className="pos-access-badge">{currentProperty.access_level}</span>
            )}
          </div>
        )}

        {/* Navigation - flat list, same patterns as DesktopLayout */}
        <nav className="sidebar-nav pos-nav">
          {posNavItems.map(item => (
            <NavLink
              key={item.path}
              to={getNavPath(item.path)}
              end={item.end}
              className={({ isActive }) => `nav-link pos-nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer - mirrors DesktopLayout user menu */}
        <div className="sidebar-footer pos-sidebar-footer">
          <div className="user-menu-container pos-user-menu">
            <button
              className="user-menu-trigger pos-user-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="user-avatar pos-user-avatar">
                {user?.first_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="user-menu-info pos-user-info">
                <div className="user-name pos-user-name">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="user-email pos-user-email">{user?.email}</div>
              </div>
              <span className="user-menu-arrow">{userMenuOpen ? '▲' : '▼'}</span>
            </button>

            {userMenuOpen && (
              <div className="user-menu-dropdown pos-user-dropdown">
                {portalAccess.admin && (
                  <Link
                    to="/"
                    className="user-menu-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <span>⚡</span>
                    <span>Operations Portal</span>
                  </Link>
                )}
                {portalAccess.ambassador && (
                  <Link
                    to="/ambassador"
                    className="user-menu-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <span>⭐</span>
                    <span>Ambassador Portal</span>
                  </Link>
                )}
                {(portalAccess.admin || portalAccess.ambassador) && (
                  <div className="user-menu-divider" />
                )}
                <button
                  className="user-menu-item user-menu-signout pos-signout"
                  onClick={() => {
                    signOut();
                    setUserMenuOpen(false);
                  }}
                >
                  <span>🚪</span>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>

          {/* Sentinel Branding */}
          <div className="pos-sentinel-footer">
            <div className="pos-sentinel-name">Sentinel Business Management</div>
            <div className="pos-sentinel-powered">Powered by CleanMyBed</div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="pos-mobile-header">
        <button
          className="pos-mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className="pos-mobile-header-center">
          <span className="pos-mobile-page-title">{currentPage?.label || 'Dashboard'}</span>
          {currentProperty && (
            <span className="pos-mobile-property">{currentProperty.property_name}</span>
          )}
        </div>
        <div className="pos-mobile-spacer" />
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="pos-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - mirrors DesktopLayout */}
      <main className="main-content pos-main-content">
        <header className="top-bar pos-top-bar">
          <div className="pos-header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={toggleSidebar}
              title={sidebarHidden ? 'Show menu (Ctrl+B)' : 'Hide menu (Ctrl+B)'}
              aria-label={sidebarHidden ? 'Show menu' : 'Hide menu'}
              aria-pressed={sidebarHidden}
            >
              <PanelIcon />
            </button>
            <h1 className="page-title pos-page-title">{currentPage?.label || 'Dashboard'}</h1>
            {currentProperty && (
              <span className="pos-header-property">
                <span className="pos-header-property-icon">🏨</span>
                {currentProperty.property_name}
              </span>
            )}
          </div>
          <div className="top-bar-actions">
            <div id="page-actions"></div>
          </div>
        </header>

        <div className="content-area pos-content-area">
          {children}
        </div>
      </main>
    </div>
  );
}

export default POSLayout;
